import { gql } from '@apollo/client/core'

/**
 * The arc GraphQL fragments, lifted out of ArcService as plain module-level data.
 *
 * `userService.getUserReports` interpolates `arcReviewClaimFragments.reviewClaim` into its own
 * query, and reaching it through `@inject('ArcService')` was UserService's *only* dependency on
 * ArcService — a 2,613-line service pulled in for one template string. ArcService still exposes
 * both as readonly members so its interface and consumers are untouched until stage 6 splits it.
 */
export const arcFragments = {
  claim: gql`
    fragment ArcClaimFragment on ArcClaimDto {
      id,
      createdDate,
      status,
      statusDate,
      cancellationReason,
      cancellationType,
      downloadCode,
      downloadCodeExpirationDate,
      reviewFlags,
      optionalReviewFlags,
      requiredReviewCount,
      state,
      selectedAudioSiteFlag,
      authorOwnsArc,
      lastReadCfi,
      dueDate,
      overdueDays,
      canFollowTeam,
      reviewerStatus,
      user {
        id
        name,
        image
      },
      review {
        id,
        rating,
        title,
        review,
        reviewLanguage,
        translatedReview,
        privateFeedback,
        disclaimer,
        createdDate,
        updatedDate
        sites {
          id,
          link,
          site,
          status,
          statusDate,
          statusReason,
          exempt,
          state
        }
      }
    }
  `,
  arc: gql`
    fragment ArcFragment on ArcDto {
      id,
      isDraft,
      status,
      userId,
      book {
        id,
        bookCover,
        title,
        seriesName,
        seriesNumber,
        penName,
        penNameType,
        wordCount,
        authorPenName {
          id,
          name,
          image,
          firstName,
          averageRating,
          reviewCount,
          followers {
            id
          }
        },
        linkAmazonAsin,
        linkAmazonAsinPrint,
        linkAudibleUs,
        linkAudibleUk,
        linkBarnes,
        linkBookbub,
        linkGoodreads,
        linkGoogle,
        linkSmashwords,
        linkKobo,
        linkITunes,
        linkITunesAudio,
        linkBarnesAudio,
        linkGoogleAudio,
        linkGoodreadsAudio,
        linkKoboAudio,
        linkBookbubAudio,
        linkStoryGraph,
        linkStoryGraphAudio,
        description,
        audience,
        keywords,
        releaseDate,
        categories {
          id,
          name,
          translations {
            locale
            name
          }
        },
        userId,
        epubFile,
        epubPreviewFile,
        epubFileSizeBytes,
        pdfFile,
        pdfFileSizeBytes,
        hasEpubFile,
        hasPdfFile,
        language
      },
      arcTeam {
        id,
        name,
        description,
        defaultDisclaimer,
        userIsFollowing,
        averageRating,
        reviewCount,
        arcTeamMembers {
          id,
          userId,
          status
        }
      },
      claims {
        id,
        review {
          id,
          rating,
          sites {
            id,
            status
          }
        }
      },
      stats {
        reviewCount,
        averageRating,
        delayedClaimed,
        cancelledCount,
        newReviewCount,
        newReviewActivityCount,
        nextNotification {
          statusDate,
          code
        }
      }
      specialInformation,
      arcType,
      discoverableType,
      participationType,
      copyCountType,
      copyCount,
      seriesLimitType,
      reviewFlags,
      optionalReviewFlags,
      requiredReviewCount,
      optionalReviewCount,
      calculatedReviewFlags,
      calculatedOptionalReviewFlags,
      calculatedRequiredReviewCount,
      calculatedOptionalReviewCount,
      calculatedRequiredSitesCount,
      liveDate,
      liveDateStr,
      reviewDays,
      stopDownloadDate,
      stopDownloadDateStr,
      neverEndingPrivate,
      participationPreventionType,
      siteWideParticipationPreventionType,
      dueDate,
      dueDateStr,
      dueCutOffDate,
      dueCutOffDateStr,
      vipExclusiveAccessDays,
      importedId,
      alertFlags,
      viewCount,
      impressionCount,
      fileUpdateNotificationSentDate,
      canClaim,
      isSaved,
      saveCount
    }
  `,
  /**
   * The Recommended score breakdown. The API fills it in for staff only, on the feed's Recommended sort, and its
   * staffOnly field guard blanks it for everyone else - so only the reader feed asks for it [sc-28740].
   */
  arcScoreDebug: gql`
    fragment ArcScoreDebugFragment on ArcDto {
      qualityScore,
      qualityEngagementIdx,
      qualityCatCpi,
      qualityClaims,
      qualityPageCount,
      qualityPageFactor,
      qualityAuthorFactor,
      qualityFreshBookArcs,
      qualityFollowFactor,
      qualityCategoryFitFactor,
      qualityCategoryFits {
        categoryId,
        score,
        reason
      }
    }
  `,
  /**
   * The audiobook stores' code lists, kept out of `ArcFragment` because the reader app shares that one. The API
   * sends them only to the campaign's owner and to staff, so only the author's own operations spread this in;
   * a reader's code reaches them on their claim [sc-28919].
   */
  arcDownloadCodes: gql`
    fragment ArcDownloadCodesFragment on ArcDto {
      usDownloadCodes,
      ukDownloadCodes,
      authorsDirectDownloadCodes,
      koboDownloadCodes
    }
  `
}

export const arcReviewClaimFragments = {
  reviewClaim: gql`
    fragment ArcReviewClaimFragment on ArcClaimDto {
      ...ArcClaimFragment,
      arc {
        id,
        dueDate,
        dueCutOffDate,
        viewCount,
        impressionCount,
        book {
          id,
          title,
          penName,
          linkAmazonAsin,
          linkAudibleUs,
          linkAudibleUk,
          linkBarnes,
          linkBookbub,
          linkGoodreads,
          linkGoogle,
          linkSmashwords,
          linkKobo,
          linkITunes,
          linkITunesAudio,
          linkBarnesAudio,
          linkGoogleAudio,
          linkGoodreadsAudio,
          linkKoboAudio,
          linkBookbubAudio,
          linkStoryGraph,
          linkStoryGraphAudio,
          authorPenName {
            id,
            name,
            image
          },
          bookCover,
          description,
          userId,
          epubFile,
          epubPreviewFile
        },
        arcTeam {
          id,
          arcTeamMembers {
            id
            status,
            arcTeamId,
            user {
              id
            }
          }
        }
      }
    },
    ${arcFragments.claim}
  `
}
